import { IconButton } from "@fluentui/react";
import { IChartProps, getNextColor } from "@fluentui/react-charting";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LineChartComponent } from "../../../ChartComponents/LineChartFluent";
import { breakpoints } from "../../../helper/ScreenBreakPoints";
import CustomCard from "../../../SharedComponents/CustomCard";
import { iconButtonStyles } from "../../../SharedComponents/Styles/CustomCardWithMetrics.styles";
import { selectViewport } from "../../../slices/UI.slice";
import { useAppSelector } from "../../../store/hooks";
import { getCustomerFeatureChartStyles } from "./CustomerFeatureChart.styles";

export const CustomerFeatureChart: React.FC<any> = ({ carouseldata, granularity = "Monthly" }) => {
  const [cardWidth, setCardWidth] = useState<number>(300);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const [, setOffsetWidth] = useState(resizedWidth);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateWidth = () => {
    if (!containerRef.current) return;

    let itemsPerRow = 1;
    for (const bp of breakpoints) {
      if (window.innerWidth >= bp.width) {
        itemsPerRow = bp.items;
        break;
      }
    }

    const availableWidth = containerRef.current.offsetWidth;
    const newCardWidth = Math.floor(availableWidth / itemsPerRow) - 16;
    setCardWidth(newCardWidth);
  };

  useLayoutEffect(() => {
    if (containerRef.current) {
      calculateWidth();
    }
  });

  useEffect(() => {
    setOffsetWidth(resizedWidth);
  }, [resizedWidth]);

  useEffect(() => {
    setCurrentDate(new Date()); // Reset to today when granularity changes
  }, [granularity]);

  const styles = getCustomerFeatureChartStyles(cardWidth);

  const GetItemData = (item: any, idx: number) => {
    // Transform raw data to chart format
    const chartLines = item.data.map((m: { name: any; data: any[] }, idx: number) => ({
      legend: m.name,
      data: m.data.map((m: any) => ({
        x: new Date(m.snapshotDate),
        y: Number(m.currentValue)
      })),
      color: getNextColor(idx),
      lineOptions: {
        lineBorderWidth: "4"
      }
    }));

    // For daily granularity, implement windowing with navigation
    if (granularity === "Daily") {
      const allDataPoints = chartLines
        .flatMap((line: any) => line.data)
        .sort((a: any, b: any) => a.x.getTime() - b.x.getTime());

      if (allDataPoints.length === 0) {
        return (
          <div key={idx} style={{ width: "100%" }}>
            <CustomCard cardHeaderTitle={item.name} kustoLink={item.kustoLink}>
              <div style={{ padding: 16, textAlign: "center" }}>No data available</div>
            </CustomCard>
          </div>
        );
      }

      // Calculate 30-day window around currentDate
      const getValidDataWindow = (centerDate: Date) => {
        const maxDate = allDataPoints[allDataPoints.length - 1].x; // Use the latest available data point

        // If centerDate is after maxDate, adjust it to maxDate
        const adjustedCenterDate = centerDate > maxDate ? maxDate : centerDate;

        const attemptDate = new Date(adjustedCenterDate);
        if (adjustedCenterDate !== maxDate) {
          attemptDate.setUTCHours(0, 0, 0, 0); // Normalize time to start of day
        }

        for (let i = 0; i < 12; i++) {
          const windowEnd = new Date(attemptDate);
          windowEnd.setUTCHours(23, 59, 59, 999); // Set to end of day
          const windowStart = new Date(attemptDate);
          windowStart.setUTCDate(windowEnd.getUTCDate() - 30);

          const linesWithData = chartLines.map((line: any) => ({
            ...line,
            data: line.data.filter((point: any) => point.x >= windowStart && point.x <= windowEnd)
          }));

          const hasData = linesWithData.some((line: any) => line.data.length > 0);
          if (hasData) {
            return { visibleLines: linesWithData, windowStart, windowEnd };
          }

          attemptDate.setUTCDate(attemptDate.getUTCDate() - 30);
        }

        return {
          visibleLines: chartLines.map((line: any) => ({ ...line, data: [] })),
          windowStart: new Date(adjustedCenterDate),
          windowEnd: new Date(adjustedCenterDate)
        };
      };

      const { visibleLines, windowStart, windowEnd } = getValidDataWindow(currentDate);

      const maxDate = allDataPoints[allDataPoints.length - 1].x;
      const hasNext = allDataPoints.some((p: any) => p.x > windowEnd) && windowEnd < maxDate;
      const hasPrevious = allDataPoints.some((p: any) => p.x < windowStart);

      // Generate tick values for X-axis
      const getAllTickValues = () => {
        return Array.from(new Set(visibleLines.flatMap((line: any) => line.data.map((p: any) => p.x.getTime())))).map(
          (timestamp: any) => new Date(timestamp)
        );
      };

      const tickValues = getAllTickValues();
      const chartData: IChartProps = { lineChartData: visibleLines };

      return (
        <div key={idx} style={{ width: "100%", padding: 8 }}>
          <CustomCard cardHeaderTitle={item.name} kustoLink={item.kustoLink}>
            <div style={{ backgroundColor: "white" }}>
              <LineChartComponent
                data={chartData}
                height={300}
                allowResize={true}
                granularity={granularity}
                selectedFamily="Security Copilot"
                tickValues={tickValues}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                position: "relative",
                zIndex: 999,
                marginTop: 12,
                top: -5,
                gap: 10
              }}
            >
              <IconButton
                iconProps={{ iconName: "ChevronLeft" }}
                title="Previous"
                onClick={() =>
                  setCurrentDate((prev) => {
                    const newDate = new Date(prev);
                    newDate.setDate(newDate.getDate() - 30);
                    return newDate;
                  })
                }
                disabled={!hasPrevious}
                styles={iconButtonStyles}
              />
              <IconButton
                iconProps={{ iconName: "ChevronRight" }}
                title="Next"
                onClick={() =>
                  setCurrentDate((prev) => {
                    const newDate = new Date(prev);
                    newDate.setDate(newDate.getDate() + 30);
                    // Don't go beyond the latest available data point
                    return newDate > maxDate ? maxDate : newDate;
                  })
                }
                disabled={!hasNext}
                styles={iconButtonStyles}
              />
            </div>
          </CustomCard>
        </div>
      );
    } else {
      // For monthly and other granularities, show all data without windowing
      const chartData: IChartProps = {
        lineChartData: chartLines
      };

      return (
        <div key={idx} style={styles.cardContainer}>
          <CustomCard cardHeaderTitle={item.name} kustoLink={item.kustoLink}>
            <div style={{ backgroundColor: "white" }}>
              <LineChartComponent
                data={chartData}
                height={300}
                width={cardWidth}
                granularity={granularity}
                allowResize
              />
            </div>
          </CustomCard>
        </div>
      );
    }
  };

  return (
    <div ref={containerRef} style={styles.container}>
      {carouseldata?.items?.length > 0 ? (
        carouseldata.items.map((m: any, idx: number) => GetItemData(m, idx))
      ) : (
        <span>No data available</span>
      )}
    </div>
  );
};
